import { useMemo, useState } from 'react'
import {
  Carousel,
  CarouselControl,
  CarouselIndicators,
  CarouselItem,
  Col,
  Row,
} from 'reactstrap'

import { type Spotlight } from '#app/models/content.server.ts'

import { SpotlightCard } from './SpotlightCard.tsx'

/** Quantidade máxima de cards por “slide” no desktop (≥ md). */
const CAROUSEL_PAGE_SIZE = 3

export type SpotlightsCarouselProps = {
  items: Spotlight[]
  id?: string
  className?: string
}

function buildPages(items: Spotlight[], pageSize: number): Spotlight[][] {
  if (items.length === 0) return []
  const size = Math.min(pageSize, items.length)
  const pageCount =
    items.length <= size ? 1 : items.length - size + 1
  return Array.from({ length: pageCount }, (_, i) =>
    items.slice(i, i + size),
  )
}

export function SpotlightsCarousel({
  items,
  id = 'novidades-landing-carousel',
  className = '',
}: SpotlightsCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [animating, setAnimating] = useState(false)

  const pages = useMemo(
    () => buildPages(items, CAROUSEL_PAGE_SIZE),
    [items],
  )

  if (items.length === 0) return null

  const lastIndex = pages.length - 1

  const next = () => {
    if (animating) return
    setActiveIndex((i) => (i >= lastIndex ? 0 : i + 1))
  }

  const previous = () => {
    if (animating) return
    setActiveIndex((i) => (i <= 0 ? lastIndex : i - 1))
  }

  const goToIndex = (newIndex: number) => {
    if (animating || newIndex === activeIndex) return
    setActiveIndex(newIndex)
  }

  const indicatorItems = pages.map((page, i) => ({
    key: i,
    altText: page[0]?.title ?? `Novidade ${i + 1}`,
    caption: '',
    pictureUrl: page[0]?.bannerImageUrl,
  }))

  const slides = pages.map((page) => (
    <CarouselItem
      key={`page-${page.map((p) => p.slug).join('-')}`}
      onExiting={() => setAnimating(true)}
      onExited={() => setAnimating(false)}
    >
      <Row className="g-3 justify-content-center">
        {page.map((item) => (
          <Col key={item.slug} md={4}>
            <div className="px-1 h-100">
              <SpotlightCard item={item} linkable />
            </div>
          </Col>
        ))}
      </Row>
    </CarouselItem>
  ))

  const sectionLabel = 'Novidades em destaque'

  return (
    <section
      className={`spotlights-carousel ${className}`}
      aria-roledescription="carousel"
      aria-label={sectionLabel}
    >
      <Carousel
        id={id}
        activeIndex={activeIndex}
        next={next}
        previous={previous}
        className="spotlights-carousel-inner"
        dark={false}
      >
        <CarouselIndicators
          items={indicatorItems}
          activeIndex={activeIndex}
          onClickHandler={goToIndex}
        />
        {slides}
        <CarouselControl
          direction="prev"
          directionText="Slide anterior"
          onClickHandler={previous}
        />
        <CarouselControl
          direction="next"
          directionText="Próximo slide"
          onClickHandler={next}
        />
      </Carousel>
    </section>
  )
}
